import {
  PodcastDurationBin,
  PodcastInstanceVariables,
  PodcastPacing,
  PodcastReframing,
} from '@lib/network/podcasts';
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded';
import AspectRatioRoundedIcon from '@mui/icons-material/AspectRatioRounded';
import CenterFocusStrongRoundedIcon from '@mui/icons-material/CenterFocusStrongRounded';
import ClosedCaptionRoundedIcon from '@mui/icons-material/ClosedCaptionRounded';
import MusicNoteRoundedIcon from '@mui/icons-material/MusicNoteRounded';
import SpeedRoundedIcon from '@mui/icons-material/SpeedRounded';
import SpellcheckRoundedIcon from '@mui/icons-material/SpellcheckRounded';
import { ReactNode } from 'react';
import css from './PodcastWidgets.module.scss';

const DURATION_LABELS: Record<PodcastDurationBin, string> = {
  '15s-1min': '15s–1min',
  '1-2min': '1–2min',
  '2-3min': '2–3min',
  mix: 'Mix',
};

const PACING_LABELS: Record<PodcastPacing, string> = {
  original: 'Original pacing',
  medium: 'Medium pacing',
  fast: 'Fast pacing',
};

const REFRAMING_LABELS: Record<PodcastReframing, string> = {
  speaker: 'Speaker',
  intercut: 'Intercut',
};

/**
 * Configuration pills for a podcast project row — duration / pacing /
 * reframing / aspect ratio / captions / music / auto-correct. Reads
 * directly off the project's `starterVariables` and renders only the
 * fields that are actually set; collapses to nothing when the project
 * has no recorded settings (queued / very early lifecycle).
 */
export const PodcastWidgets = ({ vars }: { vars: PodcastInstanceVariables }) => {
  const items: { icon: ReactNode; label: string; key: string }[] = [];
  if (vars.duration) {
    items.push({
      key: 'duration',
      icon: <AccessTimeRoundedIcon fontSize="inherit" />,
      label: DURATION_LABELS[vars.duration] ?? vars.duration,
    });
  }
  if (vars.pacing) {
    items.push({
      key: 'pacing',
      icon: <SpeedRoundedIcon fontSize="inherit" />,
      label: PACING_LABELS[vars.pacing] ?? vars.pacing,
    });
  }
  if (vars.reframing) {
    items.push({
      key: 'reframing',
      icon: <CenterFocusStrongRoundedIcon fontSize="inherit" />,
      label: REFRAMING_LABELS[vars.reframing] ?? vars.reframing,
    });
  }
  if (vars.aspect_ratio_num && vars.aspect_ratio_den) {
    items.push({
      key: 'aspect',
      icon: <AspectRatioRoundedIcon fontSize="inherit" />,
      label: `${vars.aspect_ratio_num}:${vars.aspect_ratio_den}`,
    });
  }
  if (vars.captions_enabled) {
    items.push({
      key: 'captions',
      icon: <ClosedCaptionRoundedIcon fontSize="inherit" />,
      label: 'Captions',
    });
  }
  if (vars.add_music) {
    items.push({
      key: 'music',
      icon: <MusicNoteRoundedIcon fontSize="inherit" />,
      label: 'Music',
    });
  }
  if (vars.correct_transcript) {
    items.push({
      key: 'autocorrect',
      icon: <SpellcheckRoundedIcon fontSize="inherit" />,
      label: 'Auto-correct',
    });
  }
  if (items.length === 0) return null;
  return (
    <div className={css.widgets}>
      {items.map(({ key, icon, label }) => (
        <span key={key} className={css.widget}>
          <span className={css.widgetIcon}>{icon}</span>
          <span className={css.widgetLabel}>{label}</span>
        </span>
      ))}
    </div>
  );
};
